import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from '@trashlab/sitesapp/src/organizations/redebins/components/shared/link';
import { PageSEO } from '@trashlab/sitesapp/src/organizations/redebins/template/components/shared/page-seo';
import { OrderButtons } from '@trashlab/sitesapp/src/organizations/redebins/template/components/shared/order-buttons';

const faqItems = [
  {
    question: 'What items are not allowed in a RED-E-BINS dumpster?',
    answer:
      'Hazardous waste such as oils, paints, and toxic liquids. Some items like TVs or appliances may be accepted with additional charges. We provide alternative disposal options.',
  },
  {
    question: 'Can I put hazardous materials in my dumpster?',
    answer: 'No.',
  },
  {
    question: 'What happens if prohibited items are placed in the dumpster?',
    answer: 'Pickup may be delayed or refused, and additional fees may apply.',
  },
  {
    question: 'Are there alternatives for disposing of prohibited items?',
    answer:
      'Yes. We help connect customers with proper disposal resources and partners.',
  },
];

export const ProhibitedItemsContent = () => {
  return (
    <>
      <PageSEO pathname="/prohibited-items" />

      {/* What You CAN Throw Away */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#333333] mb-10 italic text-center">
              What You CAN Throw Away
            </h2>

            <div className="grid md:grid-cols-2 gap-10 mb-12">
              {/* Household & Mixed Trash */}
              <div>
                <h3 className="text-xl font-bold text-[#333333] mb-4 italic">
                  Household &amp; Mixed Trash
                </h3>
                <ul className="space-y-2 text-[#333333] text-lg">
                  <li className="flex items-start">
                    <span className="text-green-600 mr-3 mt-1">&#10003;</span>
                    <span>Furniture, mattresses &amp; general junk</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-3 mt-1">&#10003;</span>
                    <span>Clothes, toys, boxes &amp; packaging</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-3 mt-1">&#10003;</span>
                    <span>Everyday household items</span>
                  </li>
                </ul>
              </div>

              {/* Construction & Remodeling Debris */}
              <div>
                <h3 className="text-xl font-bold text-[#333333] mb-4 italic">
                  Construction &amp; Remodeling Debris
                </h3>
                <ul className="space-y-2 text-[#333333] text-lg">
                  <li className="flex items-start">
                    <span className="text-green-600 mr-3 mt-1">&#10003;</span>
                    <span>Drywall, lumber, cabinets, flooring</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-3 mt-1">&#10003;</span>
                    <span>Siding, windows, roofing materials</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-3 mt-1">&#10003;</span>
                    <span>Demo and renovation debris</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Heavy Materials */}
            <div className="bg-gray-50 rounded-lg p-6 mb-4">
              <h3 className="text-xl font-bold text-[#333333] mb-4 italic">
                Heavy Materials <span className="text-[#E31837] font-normal text-base">(Special Rules)</span>
              </h3>
              <ul className="space-y-2 text-[#333333] text-lg">
                <li className="flex items-start">
                  <span className="text-green-600 mr-3 mt-1">&#10003;</span>
                  <span>Concrete, asphalt, brick <span className="text-gray-500">(5-yard dumpsters only, 3/4 max)</span></span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-3 mt-1">&#9888;</span>
                  <span>No bin shall exceed 3 tons (our max load weight)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-3 mt-1">&#9888;</span>
                  <span>No dirt, sand without prior approval</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Items With Restrictions */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#333333] mb-10 italic text-center">
              Items With Restrictions
            </h2>

            <div className="grid md:grid-cols-2 gap-10">
              {/* Not Allowed */}
              <div className="bg-white rounded-lg p-6 border-l-4 border-[#E31837]">
                <h3 className="text-xl font-bold text-[#333333] mb-4 italic">
                  Not Allowed
                </h3>
                <ul className="space-y-2 text-[#333333] text-lg">
                  <li className="flex items-start">
                    <span className="text-[#E31837] mr-3 mt-1">&#10007;</span>
                    <span>Hazardous materials (chemicals, paint, oils, etc.)</span>
                  </li>
                </ul>
              </div>

              {/* May Require a Fee or Approval */}
              <div className="bg-white rounded-lg p-6 border-l-4 border-yellow-500">
                <h3 className="text-xl font-bold text-[#333333] mb-4 italic">
                  May Require a Fee or Approval
                </h3>
                <ul className="space-y-2 text-[#333333] text-lg">
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-3 mt-1">&#9888;</span>
                    <span>Yard Waste</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-3 mt-1">&#9888;</span>
                    <span>Mattresses or Bulky Furniture</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-3 mt-1">&#9888;</span>
                    <span>Appliances &amp; Electronics</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-3 mt-1">&#9888;</span>
                    <span>Large or unusual items</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Not Sure? Just Call */}
      <section className="py-16 bg-[#E31837] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 italic">
              Not Sure? Just Call, Text, or Chat
            </h2>
            <p className="text-lg mb-2">
              Every situation is a little different.
            </p>
            <p className="text-lg mb-8">
              <a href="tel:9529312577" className="underline hover:text-white/80">Call</a>,{' '}
              <a href="sms:9529312577" className="underline hover:text-white/80">text</a>{' '}
              or chat with us and we'll give you a quick, clear answer so you don't have to guess.
            </p>
            <a
              href="tel:9529312577"
              className="inline-block bg-white text-[#E31837] font-bold py-4 px-10 rounded-lg text-lg hover:bg-gray-100 transition-colors shadow-lg"
            >
              Call 952-931-2577
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Schema JSON-LD in <head> for SEO */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqItems.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer,
              },
            })),
          })}
        </script>
      </Helmet>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#333333] mb-10 italic text-center">
              FAQ
            </h2>

            <div className="divide-y divide-gray-200">
              {faqItems.map((item) => (
                <FaqItem
                  key={item.question}
                  question={item.question}
                  answer={item.answer}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Ready to Book CTA */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#333333] mb-4 italic">
              Ready to Book?
            </h2>
            <p className="text-lg text-[#333333] mb-2">
              Order online in minutes.
            </p>
            <p className="text-lg text-[#333333] mb-8">
              Fast delivery. Simple pricing. No surprises.
            </p>
            <OrderButtons />
          </div>
        </div>
      </section>

      {/* Junk Removal Cross-Sell */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-50 rounded-lg p-8 md:p-12">
              <h2 className="text-2xl md:text-3xl font-bold text-[#333333] mb-4 italic">
                Simple, Flat-Rate Junk Removal You Can Count On
              </h2>
              <p className="text-[#333333] text-lg mb-4 leading-relaxed">
                If you're looking for an easy, all-in-one solution, our junk removal service is a great option — especially when you have items that may not be ideal for a dumpster or would require additional fees.
              </p>
              <p className="text-[#333333] text-lg mb-6 leading-relaxed">
                We offer simple, flat-rate pricing to handle just about anything (excluding hazardous waste), so you don't have to worry about sorting or extra charges.
              </p>
              <Link
                href="/junk-removal"
                className="inline-block bg-[#E31837] hover:bg-[#C1152E] text-white font-bold py-3 px-8 rounded-lg transition-colors text-lg"
              >
                Learn About Junk Removal
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const FaqItem = ({ question, answer }: { question: string; answer: string }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="py-5">
      <button
        type="button"
        className="flex w-full items-center justify-between text-left"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <h3 className="text-lg font-bold text-[#333333] pr-4">{question}</h3>
        <span
          className={`shrink-0 text-[#E31837] text-2xl font-bold transition-transform duration-200 ${
            isOpen ? 'rotate-45' : ''
          }`}
        >
          +
        </span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ${
          isOpen ? 'max-h-40 mt-3' : 'max-h-0'
        }`}
      >
        <p className="text-[#333333] leading-relaxed">{answer}</p>
      </div>
    </div>
  );
};
